import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Sparkles, Users } from 'lucide-react';
import type { TemplateMeta } from '../../api/templates';
import { TemplatePreviewDialog } from '../templates/TemplatePreviewDialog';
import { PreviewCard as MetaPreviewCard } from '../templates/MetaPreviewCard';
import { DEMO_CARD_USER } from './digitalCardStyles';
import { getTemplatePreviewUrl } from './templatePreviewAssets';
import { resolveTemplateGalleryCopy } from './templateGalleryProfiles';
import { buildAuthSignupUrl, savePreSignupTemplate } from '../../utils/templateOnboarding';
import { markDigitalCardSignupSource } from '../../utils/signupRedirect';
import PreviewPhoneFrame from './PreviewPhoneFrame';

type Props = {
  template: TemplateMeta | null;
  layout?: 'default' | 'linktree';
};

function categoryLabel(cat: string, t: (k: string, o?: object) => string) {
  const c = String(cat || '').toLowerCase();
  if (c.includes('artist')) return t('dcl_cat_artist', { defaultValue: 'Artist Templates' });
  return t('dcl_cat_business', { defaultValue: 'Business Templates' });
}

export const TemplateDetailPanel: React.FC<Props> = ({ template, layout = 'default' }) => {
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);

  const copy = useMemo(() => {
    if (!template) return null;
    return resolveTemplateGalleryCopy(template, t);
  }, [template, t]);

  if (!template || !copy) {
    return (
      <aside className="dcl-template-detail-panel dcl-template-detail-panel--empty" aria-live="polite">
        <div className="dcl-template-detail-empty">
          <Sparkles size={28} className="dcl-template-detail-empty-icon" />
          <h3>{t('dcl_detail_pick_title', { defaultValue: 'Select a template to explore' })}</h3>
          <p>
            {t('dcl_detail_pick_sub', {
              defaultValue:
                'Click any design to see what it looks like, who it suits best, and why it might be right for you.',
            })}
          </p>
        </div>
      </aside>
    );
  }

  const previewSrc = getTemplatePreviewUrl(template);
  const category = categoryLabel(String(template.category), t);

  const handleUse = () => {
    const cat = String(template.category || '').toLowerCase().includes('artist') ? 'artist' : 'business';
    savePreSignupTemplate(
      {
        templateMetaId: template.id,
        componentKey: template.componentKey,
        category: cat as 'business' | 'artist',
        name: template.name,
      },
      template
    );
    markDigitalCardSignupSource();
    window.location.href = buildAuthSignupUrl(template.id);
  };

  if (layout === 'linktree') {
    return (
      <aside className="dcl-lt-detail" aria-live="polite">
        <div className="dcl-lt-detail-visual">
          {previewSrc ? (
            <PreviewPhoneFrame src={previewSrc} alt={template.name} size="lg" />
          ) : null}
        </div>
        <div className="dcl-lt-detail-copy">
          <p className="dcl-lt-detail-cat">{category}</p>
          <h2 className="dcl-lt-detail-name">{template.name}</h2>
          <p className="dcl-lt-detail-overview">{copy.overview}</p>

          <div className="dcl-lt-detail-block">
            <div className="dcl-lt-detail-label">
              <Users size={16} />
              <span>{t('dcl_detail_suited_label', { defaultValue: 'Ideal for' })}</span>
            </div>
            <p className="dcl-lt-detail-suited">{copy.suitedFor}</p>
          </div>

          <ul className="dcl-lt-detail-highlights">
            {copy.highlights.map((item) => (
              <li key={item}>
                <CheckCircle2 size={16} />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="dcl-lt-detail-actions">
            <button type="button" className="dcl-btn dcl-btn--ghost dcl-btn--pill" onClick={() => setPreviewOpen(true)}>
              {t('dcl_template_preview', { defaultValue: 'Preview' })}
            </button>
            <button type="button" className="dcl-btn dcl-btn--dark dcl-btn--pill" onClick={handleUse}>
              {t('dcl_template_use', { defaultValue: 'Use This Template' })}
            </button>
          </div>
        </div>

        <TemplatePreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} title={template.name}>
          <MetaPreviewCard template={template} user={DEMO_CARD_USER} mode="full" frame="phone" />
        </TemplatePreviewDialog>
      </aside>
    );
  }

  return (
    <aside className="dcl-template-detail-panel" aria-live="polite">
      <div className="dcl-template-detail-card">
        {previewSrc ? (
          <div className="dcl-template-detail-thumb">
            <img src={previewSrc} alt={template.name} />
          </div>
        ) : null}

        <p className="dcl-template-detail-cat">{category}</p>
        <h2 className="dcl-template-detail-name">{template.name}</h2>
        <p className="dcl-template-detail-overview">{copy.overview}</p>

        <div className="dcl-template-detail-block">
          <div className="dcl-template-detail-label">
            <Users size={16} />
            <span>{t('dcl_detail_suited_label', { defaultValue: 'Ideal for' })}</span>
          </div>
          <p className="dcl-template-detail-suited">{copy.suitedFor}</p>
        </div>

        <ul className="dcl-template-detail-highlights">
          {copy.highlights.map((item) => (
            <li key={item}>
              <CheckCircle2 size={16} />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="dcl-template-detail-actions">
          <button type="button" className="dcl-btn dcl-btn--ghost dcl-btn--block" onClick={() => setPreviewOpen(true)}>
            {t('dcl_template_preview', { defaultValue: 'Preview' })}
          </button>
          <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--block" onClick={handleUse}>
            {t('dcl_template_use', { defaultValue: 'Use This Template' })}
          </button>
        </div>
      </div>

      <TemplatePreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} title={template.name}>
        <MetaPreviewCard template={template} user={DEMO_CARD_USER} mode="full" frame="phone" />
      </TemplatePreviewDialog>
    </aside>
  );
};

export default TemplateDetailPanel;
